import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { mediaUrl } from '../lib/api';

export function PageHero({ kicker, title, sub, breadcrumbs = [], image, imageAlt = '', imageAspect = 'aspect-[4/3]' }) {
  return (
    <section className="relative overflow-hidden bg-[var(--mm-cream)] border-b border-[var(--mm-line)]">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] mm-dot-halo-light rounded-full opacity-60" />
      <div className="absolute -bottom-40 -left-24 w-[520px] h-[520px] mm-dot-halo-light rounded-full opacity-40" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 relative">
        {breadcrumbs.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-[var(--mm-ink)]/60 mb-6">
            <Link to="/" className="hover:text-[var(--mm-purple)]">Home</Link>
            {breadcrumbs.map((b, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3" />
                {b.href ? <Link to={b.href} className="hover:text-[var(--mm-purple)]">{b.label}</Link> : <span className="text-[var(--mm-purple)]">{b.label}</span>}
              </span>
            ))}
          </div>
        )}
        <div className={image ? 'grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-14 items-center' : ''}>
          <div>
            {kicker && <div className="mm-kicker mb-4">{kicker}</div>}
            <h1 className="font-display text-4xl md:text-6xl font-bold text-[var(--mm-ink)] max-w-4xl leading-[1.05]">{title}</h1>
            {sub && <p className="mt-6 text-lg md:text-xl text-[var(--mm-ink)]/70 max-w-3xl leading-relaxed">{sub}</p>}
          </div>
          {image && (
            <figure className="relative">
              <div className="absolute -inset-3 rounded-[28px] border border-[var(--mm-line)] hidden lg:block" />
              <img src={mediaUrl(image)} alt={imageAlt || title} loading="lazy"
                className={`relative w-full ${imageAspect} object-cover rounded-3xl shadow-[0_28px_60px_-30px_rgba(74,18,89,.45)]`} />
            </figure>
          )}
        </div>
      </div>
    </section>
  );
}

export function Section({ children, className = '', bg = 'cream' }) {
  const bgClass = bg === 'white' ? 'bg-white' : bg === 'cream2' ? 'bg-[var(--mm-cream-2)]' : bg === 'purple' ? 'bg-[var(--mm-purple)] text-white' : 'bg-[var(--mm-cream)]';
  return <section className={`${bgClass} ${className}`}><div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">{children}</div></section>;
}

export function DotCircle({ children, size = 120, className = '' }) {
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <div className="absolute inset-0 mm-dot-halo rounded-full" />
      <div className="relative rounded-full bg-[var(--mm-purple)] flex items-center justify-center text-white" style={{ width: size * 0.62, height: size * 0.62 }}>{children}</div>
    </div>
  );
}

export function CTA({ title, sub, primary = { label: 'Talk to us', href: '/contact' }, secondary }) {
  return (
    <section className="bg-[var(--mm-purple)] text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-15" style={{background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)'}}/>
      <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full opacity-10" style={{background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)'}}/>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 relative">
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.1]">{title}</h2>
            {sub && <p className="mt-4 text-white/80 text-lg max-w-2xl">{sub}</p>}
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to={primary.href} className="inline-flex items-center gap-2 bg-white text-[var(--mm-purple)] rounded-full px-6 py-3 font-semibold text-sm hover:bg-[var(--mm-cream)] transition-colors">{primary.label}</Link>
            {secondary && <Link to={secondary.href} className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-white/10 transition-colors">{secondary.label}</Link>}
          </div>
        </div>
      </div>
    </section>
  );
}
